import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useLanguage } from '@/context/LanguageContext';
import { toast } from 'sonner';
import { FileText, LogIn, UserPlus, Store, Eye, EyeOff, Home, Info, Phone } from 'lucide-react';
import api from '@/utils/api';

/* ─── CSS keyframe animations ─── */
const STYLES = `
@keyframes floatUp {
  0%   { transform: translateY(0px) translateX(0px) scale(1);   opacity: 0; }
  10%  { opacity: 1; }
  90%  { opacity: 0.6; }
  100% { transform: translateY(-100vh) translateX(var(--dx)) scale(0.4); opacity: 0; }
}
@keyframes blobPulse {
  0%, 100% { transform: scale(1) translate(0,0); }
  33%       { transform: scale(1.12) translate(18px,-22px); }
  66%       { transform: scale(0.92) translate(-14px,16px); }
}
@keyframes blobPulse2 {
  0%, 100% { transform: scale(1) translate(0,0); }
  33%       { transform: scale(1.08) translate(-20px,18px); }
  66%       { transform: scale(1.15) translate(12px,-10px); }
}
@keyframes blobPulse3 {
  0%, 100% { transform: scale(1) translate(0,0); }
  50%       { transform: scale(1.2) translate(-8px,12px); }
}
@keyframes gridFlow {
  0%   { background-position: 0 0; }
  100% { background-position: 60px 60px; }
}
@keyframes cardEnter {
  0%   { opacity: 0; transform: translateY(32px) scale(0.97); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes fadeSlideIn {
  0%   { opacity: 0; transform: translateX(12px); }
  100% { opacity: 1; transform: translateX(0); }
}
@keyframes scanLine {
  0%   { top: -2px; opacity: 0.7; }
  100% { top: 100%; opacity: 0; }
}
@keyframes rotateSlow {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes borderGlow {
  0%,100% { box-shadow: 0 0 20px rgba(37,99,235,0.15), 0 0 0 1px rgba(255,255,255,0.12); }
  50%      { box-shadow: 0 0 40px rgba(14,165,233,0.25), 0 0 0 1px rgba(14,165,233,0.25); }
}
@keyframes typewriter {
  from { width: 0; }
  to   { width: 100%; }
}
@keyframes logoPulse {
  0%,100% { filter: drop-shadow(0 0 8px rgba(37,99,235,0.4)); }
  50%      { filter: drop-shadow(0 0 24px rgba(14,165,233,0.7)); }
}
.auth-card-enter { animation: cardEnter 0.6s cubic-bezier(0.16,1,0.3,1) forwards; }
.tab-content     { animation: fadeSlideIn 0.25s ease forwards; }
.logo-glow       { animation: logoPulse 3s ease-in-out infinite; }
`;

/* ─── Particle Canvas ─── */
const ParticleCanvas = () => {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);
  const particles = useRef([]);

  const createParticle = useCallback((canvas) => {
    const colors = ['#3b82f6','#60a5fa','#0ea5e9','#38bdf8','#93c5fd','#7dd3fc'];
    return {
      x: Math.random() * canvas.width,
      y: canvas.height + 10,
      size: Math.random() * 2.5 + 0.5,
      speed: Math.random() * 0.8 + 0.3,
      dx: (Math.random() - 0.5) * 60,
      opacity: Math.random() * 0.6 + 0.2,
      color: colors[Math.floor(Math.random() * colors.length)],
      life: 0,
      maxLife: Math.random() * 400 + 200,
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // seed initial particles
    for (let i = 0; i < 60; i++) {
      const p = createParticle(canvas);
      p.y = Math.random() * canvas.height;
      p.life = Math.random() * p.maxLife;
      particles.current.push(p);
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // spawn new
      if (particles.current.length < 80 && Math.random() < 0.4) {
        particles.current.push(createParticle(canvas));
      }

      particles.current = particles.current.filter(p => p.life < p.maxLife);

      particles.current.forEach(p => {
        p.life++;
        const progress = p.life / p.maxLife;
        const curOpacity = p.opacity * Math.sin(Math.PI * progress);
        p.y -= p.speed;
        p.x += Math.sin(p.life * 0.015) * 0.4;

        ctx.save();
        ctx.globalAlpha = curOpacity;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.shadowBlur = 6;
        ctx.shadowColor = p.color;
        ctx.fill();
        ctx.restore();
      });

      // draw faint connecting lines between nearby particles
      const pts = particles.current;
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 90) {
            ctx.save();
            ctx.globalAlpha = (1 - dist / 90) * 0.08;
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.strokeStyle = '#60a5fa';
            ctx.lineWidth = 0.5;
            ctx.stroke();
            ctx.restore();
          }
        }
      }

      animRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [createParticle]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
};

/* ─── Mouse parallax hook ─── */
const useMouseParallax = () => {
  const [pos, setPos] = useState({ x: 0.5, y: 0.5 });
  useEffect(() => {
    const handle = (e) => {
      setPos({ x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight });
    };
    window.addEventListener('mousemove', handle, { passive: true });
    return () => window.removeEventListener('mousemove', handle);
  }, []);
  return pos;
};

/* ─── Scan line ─── */
const ScanLine = () => {
  const [top, setTop] = useState(-2);
  useEffect(() => {
    let raf;
    let start = null;
    const duration = 3500;
    const animate = (ts) => {
      if (!start) start = ts;
      const pct = ((ts - start) % duration) / duration;
      setTop(pct * 102 - 2);
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <div
      className="absolute left-0 right-0 pointer-events-none"
      style={{
        top: `${top}%`,
        height: 1,
        background: 'linear-gradient(90deg, transparent, rgba(56,189,248,0.25), transparent)',
        zIndex: 1,
      }}
    />
  );
};

/* ─── Reusable UI components ─── */
const GradientButton = ({ children, disabled, type = 'button', onClick }) => (
  <button
    type={type}
    disabled={disabled}
    onClick={onClick}
    style={{
      background: disabled
        ? 'rgba(255,255,255,0.12)'
        : 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 60%, #0ea5e9 100%)',
      boxShadow: disabled ? 'none' : '0 4px 24px rgba(37,99,235,0.4)',
    }}
    className="w-full py-3 px-6 rounded-xl text-white font-semibold text-sm tracking-wide transition-all duration-300 hover:opacity-90 hover:scale-[1.01] hover:-translate-y-0.5 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
  >
    {children}
  </button>
);

const ConsentCheckbox = ({ id, checked, onChange, children }) => (
  <label htmlFor={id} className="flex items-start gap-2.5 cursor-pointer group">
    <div className="relative mt-0.5 flex-shrink-0">
      <input id={id} type="checkbox" checked={checked} onChange={onChange} required className="sr-only" />
      <div
        className="w-4 h-4 rounded transition-all duration-150"
        style={{
          background: checked ? 'linear-gradient(135deg, #1d4ed8, #0ea5e9)' : 'rgba(255,255,255,0.07)',
          border: checked ? '1px solid transparent' : '1px solid rgba(255,255,255,0.25)',
          boxShadow: checked ? '0 0 8px rgba(37,99,235,0.4)' : 'none',
        }}
      >
        {checked && (
          <svg className="w-4 h-4 text-white p-0.5" viewBox="0 0 16 16" fill="none">
            <path d="M3 8l3.5 3.5L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
    </div>
    <span className="text-xs text-white/50 group-hover:text-white/70 transition-colors leading-relaxed">{children}</span>
  </label>
);

const GlassInput = ({ id, type, placeholder, value, onChange, required }) => {
  const [show, setShow] = useState(false);
  const [focused, setFocused] = useState(false);
  const isPassword = type === 'password';
  return (
    <div className="relative">
      <input
        id={id}
        type={isPassword && show ? 'text' : type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/40 outline-none transition-all duration-200 pr-10"
        style={{
          background: focused ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.07)',
          border: focused ? '1px solid rgba(37,99,235,0.7)' : '1px solid rgba(255,255,255,0.15)',
          boxShadow: focused ? '0 0 0 3px rgba(37,99,235,0.15), inset 0 1px 0 rgba(255,255,255,0.05)' : 'none',
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      {isPassword && (
        <button type="button" tabIndex={-1} onClick={() => setShow(s => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
};

const GlassLabel = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="block text-xs font-medium text-white/60 mb-1.5 uppercase tracking-wider">
    {children}
  </label>
);

const GoogleLogo = () => (
  <svg width="20" height="20" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
    <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
);

const Divider = ({ label = 'veya' }) => (
  <div className="relative flex items-center my-3">
    <div className="flex-1 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
    <span className="mx-3 text-xs px-2 py-0.5 rounded-full"
      style={{ color: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
      {label}
    </span>
    <div className="flex-1 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
  </div>
);

const GoogleButton = ({ label = 'Google ile Devam Et' }) => {
  const [hov, setHov] = useState(false);
  return (
    <button type="button" onClick={() => { window.location.href = '/api/auth/google'; }}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl text-sm font-semibold transition-all duration-200"
      style={{
        background: hov ? 'rgba(255,255,255,0.13)' : 'rgba(255,255,255,0.08)',
        border: hov ? '1px solid rgba(255,255,255,0.28)' : '1px solid rgba(255,255,255,0.15)',
        color: '#fff',
        boxShadow: hov ? '0 0 20px rgba(66,133,244,0.2)' : 'none',
        transform: hov ? 'translateY(-1px)' : 'none',
      }}>
      <div style={{ width:28,height:28,borderRadius:7,background:'#fff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,boxShadow:'0 1px 4px rgba(0,0,0,0.25)' }}>
        <GoogleLogo />
      </div>
      {label}
    </button>
  );
};

const SideNavItem = ({ icon: Icon, label, to }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Link to={to}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        display:'flex', alignItems:'center', gap: hovered ? 10 : 0,
        padding:'10px 12px', borderRadius:12,
        color: hovered ? '#60a5fa' : 'rgba(255,255,255,0.38)',
        background: hovered ? 'rgba(255,255,255,0.06)' : 'transparent',
        width: hovered ? 148 : 44, overflow:'hidden',
        transition:'width .22s ease, color .2s, background .2s',
        textDecoration:'none', whiteSpace:'nowrap', flexShrink:0,
        boxShadow: hovered ? '0 0 16px rgba(96,165,250,0.15)' : 'none',
      }}>
      <Icon style={{ width:20, height:20, flexShrink:0 }} />
      <span style={{ fontSize:13, fontWeight:600, opacity: hovered ? 1 : 0, transition:'opacity .15s ease' }}>
        {label}
      </span>
    </Link>
  );
};

/* ─── Animated Ring decoration ─── */
const AnimatedRings = ({ mouse }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
    {/* Large rotating ring top-right */}
    <div style={{
      position:'absolute', top:'-15%', right:'-10%',
      width:600, height:600,
      border:'1px solid rgba(59,130,246,0.08)',
      borderRadius:'50%',
      animation:'rotateSlow 40s linear infinite',
    }} />
    <div style={{
      position:'absolute', top:'-10%', right:'-6%',
      width:500, height:500,
      border:'1px solid rgba(14,165,233,0.06)',
      borderRadius:'50%',
      animation:'rotateSlow 28s linear infinite reverse',
    }} />
    {/* Small ring bottom-left */}
    <div style={{
      position:'absolute', bottom:'-12%', left:'-8%',
      width:400, height:400,
      border:'1px solid rgba(99,179,237,0.06)',
      borderRadius:'50%',
      animation:'rotateSlow 35s linear infinite',
    }} />
    {/* Mouse-parallax glow */}
    <div style={{
      position:'absolute',
      left:`${mouse.x * 100}%`,
      top:`${mouse.y * 100}%`,
      width:400, height:400,
      transform:'translate(-50%,-50%)',
      background:'radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)',
      borderRadius:'50%',
      transition:'left 0.8s ease, top 0.8s ease',
      pointerEvents:'none',
    }} />
  </div>
);

/* ─── Main Component ─── */
const AuthPage = () => {
  const navigate      = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loginWithToken, register } = useAuth();
  const { theme, setThemeForContext } = useTheme();
  const { tr } = useLanguage();
  const mouse = useMouseParallax();

  const TABS = [
    { id: 'login',    label: tr('auth.tabLogin'),    icon: LogIn },
    { id: 'register', label: tr('auth.tabRegister'), icon: UserPlus },
    { id: 'store',    label: tr('auth.tabStore'),    icon: Store },
  ];

  const [activeTab,    setActiveTab]    = useState(() => {
    const tab = searchParams.get('tab');
    return ['login','register','store'].includes(tab) ? tab : 'login';
  });
  const [tabKey,       setTabKey]       = useState(0);
  const [loading,      setLoading]      = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const [showForgot,   setShowForgot]   = useState(false);
  const [forgotEmail,  setForgotEmail]  = useState('');
  const [forgotLoading,setForgotLoading]= useState(false);
  const [forgotSent,   setForgotSent]   = useState(false);
  const [cardVisible,  setCardVisible]  = useState(false);

  const [loginData,    setLoginData]    = useState({ email:'', password:'' });
  const [registerData, setRegisterData] = useState({ full_name:'', email:'', phone:'', password:'', confirm_password:'' });
  const [registerConsent, setRegisterConsent] = useState({ terms:false, kvkk:false });
  const [storeData,    setStoreData]    = useState({ name:'', contact_name:'', phone:'', email:'', address:'', password:'', confirm_password:'' });
  const [storeConsent, setStoreConsent] = useState({ terms:false, kvkk:false });

  const switchTab = (id) => {
    setActiveTab(id);
    setTabKey(k => k + 1);
  };

  useEffect(() => {
    setThemeForContext('landing');
    const t = setTimeout(() => setCardVisible(true), 80);

    const loadSiteSettings = async () => {
      try { const r = await api.get('/site-settings'); setSiteSettings(r.data); }
      catch {}
    };
    loadSiteSettings();

    const token      = searchParams.get('token');
    const oauthError = searchParams.get('error');
    if (token) {
      setLoading(true);
      loginWithToken(token).then(result => {
        if (result.success) { toast.success('Google ile giriş başarılı!'); navigate('/dashboard'); }
        else { toast.error('Giriş başarısız. Lütfen tekrar deneyin.'); setLoading(false); }
      });
    } else if (oauthError) {
      const messages = {
        google_denied:'Google girişi iptal edildi.',
        google_token_failed:'Google doğrulama başarısız.',
        google_no_email:'Google hesabından e-posta alınamadı.',
        account_disabled:'Hesabınız devre dışı bırakılmış.',
      };
      toast.error(messages[oauthError] || 'Giriş başarısız.');
    }
    return () => clearTimeout(t);
  }, []);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { toast.error('Lütfen e-posta adresinizi girin.'); return; }
    setForgotLoading(true);
    try { await api.post('/auth/forgot-password', { email: forgotEmail }); setForgotSent(true); }
    catch { toast.error('Bir hata oluştu. Lütfen tekrar deneyin.'); }
    setForgotLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const result = await login(loginData.email, loginData.password);
      if (result.success) { toast.success('Giriş başarılı!'); navigate('/dashboard'); }
      else toast.error(result.error || 'Giriş başarısız. Lütfen tekrar deneyin.');
    } catch { toast.error('Bağlantı hatası. Lütfen internet bağlantınızı kontrol edin.'); }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerConsent.terms || !registerConsent.kvkk) { toast.error('Devam etmek için tüm onayları vermeniz gerekmektedir.'); return; }
    if (registerData.password !== registerData.confirm_password) { toast.error('Şifreler eşleşmiyor'); return; }
    setLoading(true);
    try {
      const result = await register({ full_name:registerData.full_name, email:registerData.email, phone:registerData.phone||undefined, password:registerData.password });
      if (result.success) { toast.success('Kayıt başarılı! Yönlendiriliyorsunuz...'); navigate('/dashboard'); }
      else toast.error(result.error || 'Kayıt başarısız. Lütfen tekrar deneyin.');
    } catch { toast.error('Bağlantı hatası. Lütfen internet bağlantınızı kontrol edin.'); }
    setLoading(false);
  };

  const handleStoreApplication = async (e) => {
    e.preventDefault();
    if (!storeConsent.terms || !storeConsent.kvkk) { toast.error('Devam etmek için tüm onayları vermeniz gerekmektedir.'); return; }
    if (!storeData.password || !storeData.confirm_password) { toast.error('Lütfen şifre belirleyiniz'); return; }
    if (storeData.password !== storeData.confirm_password) { toast.error('Şifreler eşleşmiyor'); return; }
    if (storeData.password.length < 6) { toast.error('Şifre en az 6 karakter olmalıdır'); return; }
    setLoading(true);
    try {
      await api.post('/stores/apply', { name:storeData.name, phone:storeData.phone, email:storeData.email, address:storeData.address, password:storeData.password });
      toast.success('Başvurunuz kaydedildi! Yönetici tarafından incelenecektir.');
      setStoreData({ name:'', contact_name:'', phone:'', email:'', address:'', password:'', confirm_password:'' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Başvuru başarısız. Lütfen tekrar deneyin.');
    }
    setLoading(false);
  };

  const logoSrc = (theme === 'dark' ? siteSettings?.login_logo_dark_url : siteSettings?.login_logo_light_url) || siteSettings?.logo_url;

  return (
    <>
      <style>{STYLES}</style>
      <div className="min-h-screen flex relative overflow-hidden"
        style={{ background:'linear-gradient(160deg, #060d1a 0%, #0a1628 45%, #0d1f35 100%)' }}>

        {/* Animated grid background */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage:`linear-gradient(rgba(59,130,246,0.06) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(59,130,246,0.06) 1px, transparent 1px)`,
          backgroundSize:'60px 60px',
          animation:'gridFlow 8s linear infinite',
          zIndex:0,
        }} />

        {/* Particle canvas */}
        <ParticleCanvas />

        {/* Scan line */}
        <ScanLine />

        {/* Animated rings + parallax glow */}
        <AnimatedRings mouse={mouse} />

        {/* Animated blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex:0 }}>
          <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
            style={{ background:'radial-gradient(circle, #1d4ed8, transparent)', animation:'blobPulse 12s ease-in-out infinite' }} />
          <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
            style={{ background:'radial-gradient(circle, #0ea5e9, transparent)', animation:'blobPulse2 15s ease-in-out infinite' }} />
          <div className="absolute top-1/3 right-1/4 w-72 h-72 rounded-full opacity-10 blur-3xl"
            style={{ background:'radial-gradient(circle, #6366f1, transparent)', animation:'blobPulse3 10s ease-in-out infinite' }} />
          <div className="absolute top-2/3 left-1/4 w-56 h-56 rounded-full opacity-08 blur-3xl"
            style={{ background:'radial-gradient(circle, #0284c7, transparent)', animation:'blobPulse 18s ease-in-out infinite reverse' }} />
        </div>

        {/* ── Left Icon Sidebar ── */}
        <div className="hidden lg:flex flex-col items-start justify-center gap-3 relative"
          style={{ width:64, flexShrink:0, borderRight:'1px solid rgba(255,255,255,0.05)', overflow:'visible', zIndex:10 }}>
          <SideNavItem icon={Home}  label={tr('auth.sideHome')}    to="/" />
          <SideNavItem icon={Info}  label={tr('auth.sideAbout')}   to="/hakkimizda" />
          <SideNavItem icon={Phone} label={tr('auth.sideContact')} to="/iletisim" />
        </div>

        {/* ── Main Content ── */}
        <div className="flex-1 flex items-center justify-center p-4 lg:p-12 relative" style={{ zIndex:10 }}>
          <div className="w-full max-w-md"
            style={{ opacity: cardVisible ? 1 : 0, animation: cardVisible ? 'cardEnter 0.65s cubic-bezier(0.16,1,0.3,1) forwards' : 'none' }}>

            {/* Logo & Branding */}
            <div className="text-center mb-8">
              <Link to="/">
                {logoSrc ? (
                  <img src={logoSrc} alt="Logo" className="h-20 mx-auto mb-4 object-contain logo-glow" />
                ) : (
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 logo-glow"
                    style={{ background:'linear-gradient(135deg, #1d4ed8, #0ea5e9)', boxShadow:'0 8px 32px rgba(37,99,235,0.4)' }}>
                    <FileText className="h-8 w-8 text-white" />
                  </div>
                )}
              </Link>
              <h1 className="text-2xl font-bold text-white mb-1 tracking-tight">
                {siteSettings?.site_title || 'Coldwell Banker Nefesada'}
              </h1>
              <p className="text-white/50 text-sm">
                {siteSettings?.site_description || 'Kurumsal rapor yönetim platformu'}
              </p>
            </div>

            {/* Frosted glass card */}
            <div className="rounded-2xl p-7 shadow-2xl relative overflow-hidden"
              style={{
                background:'rgba(255,255,255,0.06)',
                backdropFilter:'blur(28px)',
                WebkitBackdropFilter:'blur(28px)',
                animation:'borderGlow 4s ease-in-out infinite',
              }}>

              {/* Inner top shine */}
              <div className="absolute top-0 left-0 right-0 h-px"
                style={{ background:'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)' }} />

              {/* Tabs */}
              <div className="flex gap-1 mb-7 p-1 rounded-xl" style={{ background:'rgba(0,0,0,0.25)' }}>
                {TABS.map(({ id, label, icon: Icon }) => (
                  <button key={id} onClick={() => switchTab(id)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-xs font-semibold transition-all duration-200"
                    style={activeTab === id
                      ? { background:'linear-gradient(135deg, #1d4ed8, #2563eb)', color:'#fff', boxShadow:'0 4px 15px rgba(29,78,216,0.45)' }
                      : { color:'rgba(255,255,255,0.45)' }
                    }>
                    <Icon className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{label}</span>
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div key={tabKey} className="tab-content">

                {/* Login */}
                {activeTab === 'login' && !showForgot && (
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <GlassLabel htmlFor="login-email">{tr('auth.email')}</GlassLabel>
                      <GlassInput id="login-email" type="email" placeholder="email@example.com"
                        value={loginData.email} onChange={(e) => setLoginData({...loginData, email:e.target.value})} required />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <GlassLabel htmlFor="login-password">{tr('auth.password')}</GlassLabel>
                        <button type="button" onClick={() => { setShowForgot(true); setForgotSent(false); setForgotEmail(loginData.email); }}
                          className="text-xs text-blue-400 hover:text-blue-300 transition-colors">
                          {tr('auth.forgotPassword')}
                        </button>
                      </div>
                      <GlassInput id="login-password" type="password" placeholder="••••••••"
                        value={loginData.password} onChange={(e) => setLoginData({...loginData, password:e.target.value})} required />
                    </div>
                    <div className="pt-2">
                      <GradientButton type="submit" disabled={loading}>
                        {loading ? tr('auth.loggingIn') : tr('auth.loginBtn')}
                      </GradientButton>
                    </div>
                    <Divider label={tr('auth.orWith')} />
                    <GoogleButton label={tr('auth.googleLogin')} />
                  </form>
                )}

                {/* Forgot Password */}
                {activeTab === 'login' && showForgot && (
                  <div className="space-y-4">
                    <button type="button" onClick={() => { setShowForgot(false); setForgotSent(false); }}
                      className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 transition-colors mb-2">
                      {tr('auth.backToLogin')}
                    </button>
                    {!forgotSent ? (
                      <form onSubmit={handleForgotPassword} className="space-y-4">
                        <div>
                          <h3 className="text-white font-semibold text-base mb-1">{tr('auth.forgotPasswordTitle')}</h3>
                          <p className="text-white/50 text-xs mb-4">{tr('auth.forgotPasswordDesc')}</p>
                          <GlassLabel htmlFor="forgot-email">{tr('auth.emailAddress')}</GlassLabel>
                          <GlassInput id="forgot-email" type="email" placeholder="email@example.com"
                            value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required />
                        </div>
                        <GradientButton type="submit" disabled={forgotLoading}>
                          {forgotLoading ? tr('auth.sending') : tr('auth.sendResetLink')}
                        </GradientButton>
                      </form>
                    ) : (
                      <div className="text-center py-4">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4"
                          style={{ background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.3)' }}>
                          <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <h3 className="text-white font-semibold text-base mb-2">{tr('auth.emailSent')}</h3>
                        <p className="text-white/50 text-sm leading-relaxed">
                          {tr('auth.orWith')} <strong className="text-white/70">{forgotEmail}</strong> {tr('auth.emailSentDesc')}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Register */}
                {activeTab === 'register' && (
                  <div>
                    <GoogleButton label={tr('auth.googleRegister')} />
                    <Divider label={tr('auth.orWithEmail')} />
                    <form onSubmit={handleRegister} className="space-y-4">
                      <div>
                        <GlassLabel htmlFor="register-name">{tr('auth.fullName')}</GlassLabel>
                        <GlassInput id="register-name" type="text" placeholder={tr('auth.fullName')}
                          value={registerData.full_name} onChange={(e) => setRegisterData({...registerData, full_name:e.target.value})} required />
                      </div>
                      <div>
                        <GlassLabel htmlFor="register-email">{tr('auth.email')}</GlassLabel>
                        <GlassInput id="register-email" type="email" placeholder="email@example.com"
                          value={registerData.email} onChange={(e) => setRegisterData({...registerData, email:e.target.value})} required />
                      </div>
                      <div>
                        <GlassLabel htmlFor="register-phone">{tr('auth.phoneOptional')}</GlassLabel>
                        <GlassInput id="register-phone" type="tel" placeholder="05XX XXX XX XX"
                          value={registerData.phone} onChange={(e) => setRegisterData({...registerData, phone:e.target.value})} />
                      </div>
                      <div>
                        <GlassLabel htmlFor="register-password">{tr('auth.password')}</GlassLabel>
                        <GlassInput id="register-password" type="password" placeholder="••••••••"
                          value={registerData.password} onChange={(e) => setRegisterData({...registerData, password:e.target.value})} required />
                      </div>
                      <div>
                        <GlassLabel htmlFor="register-confirm">{tr('auth.passwordConfirm')}</GlassLabel>
                        <GlassInput id="register-confirm" type="password" placeholder="••••••••"
                          value={registerData.confirm_password} onChange={(e) => setRegisterData({...registerData, confirm_password:e.target.value})} required />
                      </div>
                      <div className="space-y-2.5 pt-1 pb-1">
                        <ConsentCheckbox id="reg-terms" checked={registerConsent.terms}
                          onChange={(e) => setRegisterConsent({...registerConsent, terms:e.target.checked})}>
                          <Link to="/kullanim-kosullari" target="_blank" className="underline underline-offset-2 opacity-80 hover:opacity-100">Kullanım Koşulları</Link>'nı okudum ve kabul ediyorum <span className="text-red-400">*</span>
                        </ConsentCheckbox>
                        <ConsentCheckbox id="reg-kvkk" checked={registerConsent.kvkk}
                          onChange={(e) => setRegisterConsent({...registerConsent, kvkk:e.target.checked})}>
                          <Link to="/kvkk" target="_blank" className="underline underline-offset-2 opacity-80 hover:opacity-100">KVKK Aydınlatma Metni</Link>'ni okudum, <Link to="/gizlilik-politikasi" target="_blank" className="underline underline-offset-2 opacity-80 hover:opacity-100">Gizlilik Politikası</Link>'nı kabul ediyorum <span className="text-red-400">*</span>
                        </ConsentCheckbox>
                      </div>
                      <div className="pt-2">
                        <GradientButton type="submit" disabled={loading}>
                          {loading ? tr('auth.registering') : tr('auth.registerBtn')}
                        </GradientButton>
                      </div>
                    </form>
                  </div>
                )}

                {/* Store Application */}
                {activeTab === 'store' && (
                  <form onSubmit={handleStoreApplication} className="space-y-3">
                    <div>
                      <GlassLabel htmlFor="store-name">{tr('auth.storeName')}</GlassLabel>
                      <GlassInput id="store-name" type="text" placeholder={tr('auth.storeName')}
                        value={storeData.name} onChange={(e) => setStoreData({...storeData, name:e.target.value})} required />
                    </div>
                    <div>
                      <GlassLabel htmlFor="store-contact">{tr('auth.contactPerson')}</GlassLabel>
                      <GlassInput id="store-contact" type="text" placeholder={tr('auth.contactPerson')}
                        value={storeData.contact_name} onChange={(e) => setStoreData({...storeData, contact_name:e.target.value})} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <GlassLabel htmlFor="store-email">{tr('auth.email')}</GlassLabel>
                        <GlassInput id="store-email" type="email" placeholder="email@example.com"
                          value={storeData.email} onChange={(e) => setStoreData({...storeData, email:e.target.value})} required />
                      </div>
                      <div>
                        <GlassLabel htmlFor="store-phone">{tr('auth.phone')}</GlassLabel>
                        <GlassInput id="store-phone" type="tel" placeholder="0530 XXX XX XX"
                          value={storeData.phone} onChange={(e) => setStoreData({...storeData, phone:e.target.value})} required />
                      </div>
                    </div>
                    <div>
                      <GlassLabel htmlFor="store-address">{tr('auth.address')}</GlassLabel>
                      <GlassInput id="store-address" type="text" placeholder={tr('auth.address')}
                        value={storeData.address} onChange={(e) => setStoreData({...storeData, address:e.target.value})} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <GlassLabel htmlFor="store-password">{tr('auth.password')}</GlassLabel>
                        <GlassInput id="store-password" type="password" placeholder="••••••••"
                          value={storeData.password} onChange={(e) => setStoreData({...storeData, password:e.target.value})} required />
                      </div>
                      <div>
                        <GlassLabel htmlFor="store-confirm">{tr('auth.passwordConfirm')}</GlassLabel>
                        <GlassInput id="store-confirm" type="password" placeholder="••••••••"
                          value={storeData.confirm_password} onChange={(e) => setStoreData({...storeData, confirm_password:e.target.value})} required />
                      </div>
                    </div>
                    <div className="space-y-2.5 pt-1 pb-1">
                      <ConsentCheckbox id="store-terms" checked={storeConsent.terms}
                        onChange={(e) => setStoreConsent({...storeConsent, terms:e.target.checked})}>
                        <Link to="/kullanim-kosullari" target="_blank" className="underline underline-offset-2 opacity-80 hover:opacity-100">Kullanım Koşulları</Link>'nı okudum ve kabul ediyorum <span className="text-red-400">*</span>
                      </ConsentCheckbox>
                      <ConsentCheckbox id="store-kvkk" checked={storeConsent.kvkk}
                        onChange={(e) => setStoreConsent({...storeConsent, kvkk:e.target.checked})}>
                        <Link to="/kvkk" target="_blank" className="underline underline-offset-2 opacity-80 hover:opacity-100">KVKK Aydınlatma Metni</Link>'ni okudum, <Link to="/gizlilik-politikasi" target="_blank" className="underline underline-offset-2 opacity-80 hover:opacity-100">Gizlilik Politikası</Link>'nı kabul ediyorum <span className="text-red-400">*</span>
                      </ConsentCheckbox>
                    </div>
                    <div className="pt-2">
                      <GradientButton type="submit" disabled={loading}>
                        {loading ? tr('auth.storeApplying') : tr('auth.storeApply')}
                      </GradientButton>
                    </div>
                    <p className="text-xs text-white/30 text-center pt-1">{tr('auth.storeNote')}</p>
                  </form>
                )}
              </div>
            </div>

            {/* Footer */}
            <p className="text-center text-white/20 text-xs mt-6">
              {siteSettings?.footer_text || `© ${new Date().getFullYear()} Coldwell Banker Nefesada`}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default AuthPage;
